// OrgOnboarding pertence sempre a um workspace (banner nao-editavel no topo).
// State machine + foot nav.
import { useState } from "react";

import { Icon } from "@/components/common/Icon";
import { OnbFootNav } from "@/components/onboarding/OnbFootNav";
import { OnbShell } from "@/components/onboarding/OnbShell";
import { useCnpjLookup } from "@/hooks/useCnpjLookup";
import { extractErrorMessage } from "@/services/axiosConfig";

import { OrgStepCertificado } from "./OrgStepCertificado";
import { OrgStepContatos } from "./OrgStepContatos";
import { OrgStepOrg } from "./OrgStepOrg";
import { OrgSuccess } from "./OrgSuccess";
import { useStepWizard } from "./useStepWizard";
import {
  ORG_INITIAL_FORM,
  ORG_STEPS,
  type OrgForm,
} from "./types";

export interface OrgAcceptPayload {
  cnpj: string;
  legal_name: string;
  nickname?: string | null;
  accepted_tos: boolean;
  password?: string | null;
  phone?: string | null;
  // SEC-030: senha da conta existente (prova de posse) quando email_exists=true.
  existing_account_password?: string | null;
  // Dados cadastrais + responsável + contatos. SEC-021: os contatos vão DENTRO
  // do accept (rota pública) — o owner recém-criado é não-verificado e tomaria
  // 403 no gate require_account_ready se gravássemos por uma rota autenticada.
  city?: string | null;
  uf?: string | null;
  cnae?: string | null;
  tax_regime?: string | null;
  legal_rep_cpf?: string | null;
  contacts?: OrgContactInput[];
  address_street?: string | null;
  address_number?: string | null;
  address_complement?: string | null;
  address_neighborhood?: string | null;
  address_zip?: string | null;
}

interface Props {
  token?: string;
  workspaceName?: string;
  // Quando false, o aceite exige password+phone (user novo). Vem do GET /invitations.
  emailExists?: boolean;
  emailMasked?: string;
  onExit?: () => void;
  // Submit do wizard — cria a org (accept público, com contatos embutidos) e
  // retorna o id da organização criada. Disparado no último step (SEC-021).
  onSubmit?: (payload: OrgAcceptPayload) => Promise<number | void>;
  // Anexa o certificado A1 na org já criada (POST .../certificate, multipart).
  onUploadCertificate?: (orgId: number, file: File, password: string) => Promise<void>;
  submitError?: string | null;
  isSubmitting?: boolean;
}

export interface OrgContactInput {
  kind: string;
  name: string;
  email?: string | null;
  phone?: string | null;
}

export function OrgOnboarding({
  workspaceName, emailExists, emailMasked, onExit, onSubmit,
  onUploadCertificate, submitError, isSubmitting,
}: Props) {
  const needsAuth = emailExists === false;
  const wsName = workspaceName || "Tributo Devido";

  const {
    stepId,
    completed,
    skipped,
    done,
    setDone,
    stepIdx,
    completedCount,
    isLast,
    markCompletedAndAdvance,
    markSkippedAndAdvance,
    goBack,
    goStep,
  } = useStepWizard(ORG_STEPS);
  const [createdOrgId, setCreatedOrgId] = useState<number | null>(null);
  const [certUploadError, setCertUploadError] = useState<string | null>(null);
  const cnpjLookup = useCnpjLookup();
  const looking = cnpjLookup.isPending;
  const [form, setForm] = useState<OrgForm>(ORG_INITIAL_FORM);

  const setField = <K extends keyof OrgForm>(k: K, v: OrgForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const setDocType = (docType: "cnpj" | "cpf") =>
    setForm((f) => ({
      ...f,
      docType,
      ...(docType === "cnpj"
        ? { cpf: "", nomePf: "" }
        : {
            cnpj: "",
            razao: "",
            fantasia: "",
            cnae: "",
            regime: "",
          }),
    }));

  const lookup = async () => {
    if (form.docType !== "cnpj") return;
    if (!form.cnpj) return;
    try {
      const r = await cnpjLookup.mutateAsync(form.cnpj);
      setForm((f) => ({
        ...f,
        razao: r.razao_social || f.razao,
        fantasia: r.nome_fantasia || f.fantasia,
        cidade: r.cidade || f.cidade,
        sede: r.uf || f.sede,
        cnae: r.cnae || f.cnae,
        regime: r.tax_regime || f.regime,
        logradouro: r.logradouro || f.logradouro,
        numero: r.numero || f.numero,
        complemento: r.complemento || f.complemento,
        bairro: r.bairro || f.bairro,
        cep: r.cep || f.cep,
      }));
    } catch {
      // CNPJ nao encontrado — usuario preenche manual.
    }
  };

  // Aceite real (não hardcoded): user novo confirma os Termos; user existente
  // (já aceitou no signup) confirma o consentimento LGPD desta org.
  const tosAccepted = needsAuth ? form.acceptAceiteTermos : form.aceiteLgpd;

  const collectContacts = (): OrgContactInput[] =>
    form.contatos
      .filter((c) => c.name.trim())
      .map((c) => ({
        kind: c.kind || "outro",
        name: c.name.trim(),
        email: c.email || null,
        phone: c.phone || null,
      }));

  // SEC-021: o accept é DEFERIDO para o fim do wizard, carregando os contatos
  // (JSON) dentro do próprio payload do accept (rota pública). Assim o owner
  // recém-criado e não-verificado não precisa bater numa rota autenticada
  // (require_account_ready → 403) só pra salvar contatos.
  // O certificado é multipart e exige o id da org já criada → fica como upload
  // best-effort logo após o accept (que seta cookies). Se o gate de verificação
  // bloquear, é tratado/sinalizado sem quebrar o fluxo (não mais engolido em
  // silêncio: ver certUploadError).
  const acceptAndFinish = async (): Promise<boolean> => {
    if (!onSubmit) {
      setDone(true);
      return true;
    }
    let orgId: number | null = createdOrgId;
    if (orgId == null) {
      try {
        const result = await onSubmit({
          cnpj: form.cnpj.replace(/\D/g, ""),
          legal_name: form.razao,
          nickname: form.nomeOrg || form.fantasia || null,
          accepted_tos: tosAccepted,
          password: needsAuth ? form.acceptPassword : null,
          phone: needsAuth ? form.acceptPhone : null,
          // SEC-030: conta existente -> reautentica com a senha do dono.
          existing_account_password: needsAuth ? null : form.existingAccountPassword || null,
          city: form.cidade || null,
          uf: form.sede || null,
          cnae: form.cnae || null,
          tax_regime: form.regime || null,
          legal_rep_cpf: form.respUsarWs ? null : form.respCpf.replace(/\D/g, "") || null,
          contacts: collectContacts(),
          address_street: form.logradouro || null,
          address_number: form.numero || null,
          address_complement: form.complemento || null,
          address_neighborhood: form.bairro || null,
          address_zip: form.cep || null,
        });
        if (typeof result === "number") {
          orgId = result;
          setCreatedOrgId(result);
        }
      } catch {
        return false; // erro propagado via submitError; nao avanca.
      }
    }
    // Certificado (multipart) — best-effort, após o accept que já setou cookies.
    if (orgId != null && onUploadCertificate && form.certFile && form.certAceiteLgpd && form.certSenha) {
      try {
        await onUploadCertificate(orgId, form.certFile, form.certSenha);
        setCertUploadError(null);
      } catch (err) {
        // Não engole mais (SEC-021): sinaliza ao usuário. O owner recém-criado
        // pode tomar 403 no gate de verificação — nesse caso anexa depois nas
        // configurações da org após verificar o e-mail/telefone.
        setCertUploadError(extractErrorMessage(err));
      }
    }
    return true;
  };

  const goNext = async () => {
    // No último step (ou quando não há próximo), dispara o accept com contatos
    // antes de finalizar; em erro, não avança (submitError já sinaliza).
    if (isLast) {
      const ok = await acceptAndFinish();
      if (!ok) return;
    }
    markCompletedAndAdvance();
  };
  const goSkip = async () => {
    if (isLast) {
      const ok = await acceptAndFinish();
      if (!ok) return;
    }
    markSkippedAndAdvance();
  };
  const exitToDashboard = () => onExit?.();

  const WorkspaceBanner = () => (
    <div className="onb-ws-banner">
      <span className="onb-ws-banner-icon">
        <Icon name="building" size={14} />
      </span>
      <div>
        <div className="onb-ws-banner-l">PERTENCE AO WORKSPACE</div>
        <div className="onb-ws-banner-v">{wsName}</div>
      </div>
      <span
        className="onb-ws-banner-lock"
        title="Não editável — vinculado ao workspace que enviou o convite"
      >
        <Icon name="shield-check" size={11} /> Vinculado
      </span>
    </div>
  );

  if (done) {
    return (
      <OnbShell
        stepId={stepId}
        STEPS={ORG_STEPS}
        completed={completed}
        skipped={skipped}
        onGoStep={goStep}
        completedCount={completedCount}
        onExit={exitToDashboard}
      >
        <OrgSuccess
          form={form}
          completed={completed}
          onResume={goStep}
          onExit={exitToDashboard}
        />
      </OnbShell>
    );
  }

  const curStep = ORG_STEPS[stepIdx];

  // Validação client-side do step atual. Só o step "org" (criação) é obrigatório.
  const orgStepValid =
    !!form.cnpj &&
    !!form.razao.trim() &&
    tosAccepted &&
    (needsAuth
      ? form.acceptPassword.length >= 8 && !!form.acceptPhone.trim()
      // SEC-030: conta existente precisa da senha do dono pra anexar o grant.
      : !!form.existingAccountPassword.trim());
  const canContinue = curStep.id === "org" ? orgStepValid : true;

  return (
    <OnbShell
      stepId={stepId}
      STEPS={ORG_STEPS}
      completed={completed}
      skipped={skipped}
      onGoStep={goStep}
      completedCount={completedCount}
      onExit={exitToDashboard}
    >
      <WorkspaceBanner />

      {stepId === "org" && (
        <OrgStepOrg
          form={form}
          setField={setField}
          setDocType={setDocType}
          lookup={lookup}
          isLooking={looking}
          workspaceName={wsName}
          needsAuth={needsAuth}
          emailMasked={emailMasked}
        />
      )}
      {stepId === "certificado" && (
        <OrgStepCertificado form={form} setField={setField} />
      )}
      {stepId === "contatos" && (
        <OrgStepContatos form={form} setField={setField} />
      )}

      <OnbFootNav
        canGoBack={stepIdx > 0}
        onBack={goBack}
        canSkip={!curStep.required}
        onSkip={goSkip}
        onNext={goNext}
        isLast={isLast}
        isSubmitting={isSubmitting}
        canContinue={canContinue}
      />
      {submitError && (
        <div className="onb-submit-error" role="alert">
          <Icon name="alert-triangle" size={14} />
          <span>{submitError}</span>
        </div>
      )}
      {certUploadError && (
        <div className="onb-submit-error" role="alert">
          <Icon name="alert-triangle" size={14} />
          <span>
            A organização foi criada, mas o certificado não pôde ser anexado agora
            ({certUploadError}). Você pode anexá-lo depois nas configurações da org.
          </span>
        </div>
      )}
    </OnbShell>
  );
}
